/**
 * Drawing the sparse point cloud, with no 3D library.
 *
 * The evaluation, since the alternative is the obvious one: three.js is ~150 KB
 * gzipped, and the whole application bundle is currently 117 KB. It would more
 * than double the download of every user, for one screen, to do what amounts to
 * `gl.drawArrays(gl.POINTS)` over one static buffer. There is no scene graph
 * here, no materials, no lighting, no animation system, no loaders - a point
 * cloud needs a matrix and a vertex buffer.
 *
 * So this is raw WebGL: about two hundred lines, no new dependency, no new
 * licence, and nothing added to the supply chain of an application that
 * currently has four runtime dependencies. If this screen ever needs meshes,
 * materials and lighting, that is the moment to reconsider - not before.
 *
 * **What it never does is invent geometry.** There is no placeholder cloud, no
 * demo model, no procedural stand-in for a file that failed to load. If the
 * bytes are not there, the viewport says so; a picture of something else
 * presented as the user's reconstruction is the exact failure this project
 * keeps writing rules about.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { type PointCloud } from "./ply";
import { compile, mvpMatrix } from "./viewerCamera";
import { framingOf } from "./viewerFraming";
import { useViewerCamera } from "./useViewerCamera";
import { capturePng, saveImage } from "./viewerCapture";
import { VIEWER_HINT, ViewerToolbar } from "./ViewerToolbar";
import {
  POINT_SIZE_CHANGED,
  POINT_SIZE_SCALE,
  readPointSize,
  writePointSize,
  type PointSize,
} from "./viewerPreferences";
import { observeViewerBackground } from "./viewerBackground";

const VERTEX_SHADER = `
attribute vec3 position;
attribute vec3 color;
uniform mat4 mvp;
uniform mediump float pointSize;
varying vec3 vColor;
void main() {
  vColor = color;
  gl_Position = mvp * vec4(position, 1.0);
  gl_PointSize = pointSize;
}
`;

// Round points, because square ones read as pixel artefacts at small sizes.
const FRAGMENT_SHADER = `
precision mediump float;
varying vec3 vColor;
uniform mediump float pointSize;
void main() {
  vec2 offset = gl_PointCoord - vec2(0.5);
  if (pointSize > 0.0 && dot(offset, offset) > 0.25) discard;
  gl_FragColor = vec4(vColor, 1.0);
}
`;

export function PointCloudViewer({ cloud, cameraLines }: { cloud: PointCloud; cameraLines?: Float32Array | undefined }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Not state: the camera changes on every mouse-move and must not re-render
  // React. The hook owns it, and the model viewer uses the same one - two
  // viewers with separately written controls is how the same gesture ends up
  // meaning two things.
  const view = useViewerCamera();
  const resizeRef = useRef<() => void>(() => {});
  /** Resolved by the next frame, with the pixels it drew. */
  const captureRef = useRef<(() => Promise<Blob | null>) | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureFailed, setCaptureFailed] = useState(false);
  const redrawRef = useRef<() => void>(() => {});
  const cameraLinesRef = useRef(cameraLines);
  useEffect(() => {
    cameraLinesRef.current = cameraLines;
    redrawRef.current();
  }, [cameraLines]);
  // The point-size preference, in a ref for the same reason the camera is: the
  // render loop reads it every frame and must not re-render React to see a new
  // value, and changing it must not tear down the WebGL context and re-upload
  // the cloud.
  const pointSize = useRef<PointSize>(readPointSize());

  // Settings and the viewer can be on screen in the same document, so a
  // same-document event is what makes a change visible without a reload.
  // `storage` would only fire in *other* tabs.
  useEffect(() => {
    const apply = () => {
      pointSize.current = readPointSize();
      redrawRef.current();
    };
    window.addEventListener(POINT_SIZE_CHANGED, apply);
    window.addEventListener("storage", apply);
    return () => {
      window.removeEventListener(POINT_SIZE_CHANGED, apply);
      window.removeEventListener("storage", apply);
    };
  }, []);


  /**
   * Save the canvas, or say it could not be saved.
   *
   * Never a blank file: a read that produced nothing reports a failure rather
   * than handing somebody an empty picture and calling it their model.
   */
  const capture = useCallback(async () => {
    const take = captureRef.current;
    if (!take || capturing) return;
    setCapturing(true);
    setCaptureFailed(false);
    try {
      const blob = await take();
      if (blob) saveImage(blob, "nokta-bulutu.png");
      else setCaptureFailed(true);
    } catch {
      setCaptureFailed(true);
    } finally {
      setCapturing(false);
    }
  }, [capturing]);

  useEffect(() => {
    if (!expanded) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  useEffect(() => {
    // The DOM node is deliberately not replaced when immersive mode changes.
    // Ask the existing renderer to measure again after the new layout lands;
    // ResizeObserver remains the primary path for later viewport changes.
    resizeRef.current();
  }, [expanded]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", {
      antialias: true,
      preserveDrawingBuffer: false,
    });
    if (!gl) {
      setFailed("Bu tarayıcıda WebGL kullanılamıyor.");
      return;
    }

    // Framed on the bulk of the cloud, clipped against all of it. A sparse
    // solve emits stray points hundreds of units out; framing on the raw
    // bounding box put the subject in the middle two per cent of the canvas.
    const framing = framingOf(cloud.positions);
    const radius = framing.sceneRadius;

    let program: WebGLProgram | null = null;
    let positionBuffer: WebGLBuffer | null = null;
    let colorBuffer: WebGLBuffer | null = null;
    let frame = 0;
    let disposed = false;
    // The clear colour, kept current by `observeViewerBackground` below
    // rather than read per frame - a computed-style read inside `draw`
    // would force a style recalculation on every animation frame.
    let clear: [number, number, number] = [0.05, 0.06, 0.08];

    try {
      const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
      const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      program = gl.createProgram();
      if (!program) throw new Error("program oluşturulamadı");
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      // The shaders are owned by the program once linked; deleting the handles
      // here is what stops them leaking for the lifetime of the context.
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) ?? "program bağlanamadı");
      }

      positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, cloud.positions, gl.STATIC_DRAW);

      // A cloud with no colour is drawn in one neutral tone rather than in a
      // palette this code invented for it.
      const colors =
        cloud.colors ?? new Uint8Array(cloud.count * 3).fill(190);
      colorBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, colors, gl.STATIC_DRAW);
    } catch (error) {
      setFailed(error instanceof Error ? error.message : "3D görüntüleyici başlatılamadı.");
      return;
    }

    let cameraBuffer: WebGLBuffer | null = null;
    let uploadedCameraLines: Float32Array | undefined;
    const positionLocation = gl.getAttribLocation(program, "position");
    const colorLocation = gl.getAttribLocation(program, "color");
    const mvpLocation = gl.getUniformLocation(program, "mvp");
    const sizeLocation = gl.getUniformLocation(program, "pointSize");

    const draw = () => {
      frame = 0;
      if (disposed || !program) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.floor(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.floor(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(clear[0], clear[1], clear[2], 1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);

      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
      gl.enableVertexAttribArray(colorLocation);
      gl.vertexAttribPointer(colorLocation, 3, gl.UNSIGNED_BYTE, true, 0, 0);

      gl.uniformMatrix4fv(
        mvpLocation,
        false,
        mvpMatrix(view.camera.current, canvas.width / canvas.height, radius),
      );
      // The preference is read through a ref rather than closed over, so
      // changing it redraws at the new size without rebuilding the WebGL
      // context and re-uploading the whole cloud.
      gl.uniform1f(sizeLocation, Math.max(1.5, 2.5 * ratio * POINT_SIZE_SCALE[pointSize.current]));
      gl.drawArrays(gl.POINTS, 0, cloud.count);
      const lines = cameraLinesRef.current;
      if (lines && lines.length > 0) {
        cameraBuffer ??= gl.createBuffer();
        if (!cameraBuffer) {
          setFailed("Kamera çizgileri yüklenemedi.");
          return;
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, cameraBuffer);
        if (uploadedCameraLines !== lines) {
          gl.bufferData(gl.ARRAY_BUFFER, lines, gl.STATIC_DRAW);
          uploadedCameraLines = lines;
        }
        gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);
        gl.disableVertexAttribArray(colorLocation);
        gl.vertexAttrib3f(colorLocation, .75, .75, .75);
        gl.uniform1f(sizeLocation, 0);
        gl.drawArrays(gl.LINES, 0, lines.length / 3);
      }


      // Read back inside the frame that drew, which is what makes this work
      // without `preserveDrawingBuffer` - the buffer is not discarded until
      // the frame is composited.
      const pending = pendingCapture;
      if (pending) {
        pendingCapture = null;
        void capturePng(gl, canvas).then(pending);
      }
    };

    let pendingCapture: ((blob: Blob | null) => void) | null = null;
    captureRef.current = () =>
      new Promise<Blob | null>((resolve) => {
        pendingCapture = resolve;
        schedule();
      });

    const schedule = () => {
      if (disposed || frame) return;
      frame = window.requestAnimationFrame(draw);
    };
    resizeRef.current = schedule;
    redrawRef.current = schedule;

    const detach = view.attach(canvas, framing, schedule);

    // A browser can take the GL context away - a tab in the background, a
    // driver reset, too many contexts open. Without this the canvas simply
    // stops updating and the reader is left looking at a black rectangle
    // wondering whether their model is broken.
    const onContextLost = (event: Event) => {
      event.preventDefault();
      setFailed("3D görünüm kesildi. Sayfayı yenileyip tekrar deneyin.");
    };
    canvas.addEventListener("webglcontextlost", onContextLost);

    const observer = new ResizeObserver(() => {
      // The aspect ratio decides the fitting distance, so a resize changes
      // what "sığdır" means. The stored home follows the viewport; the user's
      // current view stays where they put it.
      view.refit();
      schedule();
    });
    observer.observe(canvas);

    // The void behind the model follows the appearance preference. The
    // geometry's own colour never does - see `viewerBackground`.
    const stopWatchingTheme = observeViewerBackground(canvas, (rgb) => {
      clear = rgb;
      schedule();
    });

    schedule();

    // --- teardown ------------------------------------------------------------
    //
    // A WebGL context is not garbage collected promptly, and a browser only
    // allows a handful at once. Navigating between projects without this leaves
    // contexts alive until the tab is closed and eventually kills the viewer
    // with "too many contexts".
    return () => {
      disposed = true;
      captureRef.current = null;
      pendingCapture = null;
      resizeRef.current = () => {};
      redrawRef.current = () => {};
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      stopWatchingTheme();
      canvas.removeEventListener("webglcontextlost", onContextLost);
      detach();
      if (positionBuffer) gl.deleteBuffer(positionBuffer);
      if (colorBuffer) gl.deleteBuffer(colorBuffer);
      if (cameraBuffer) gl.deleteBuffer(cameraBuffer);
      if (program) gl.deleteProgram(program);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [cloud, view]);

  if (failed) {
    return (
      <div className="viewer viewer--failed" role="alert">
        <p className="viewport__state">3D görüntüleyici açılamadı</p>
        <p className="viewport__note">{failed}</p>
      </div>
    );
  }

  return (
    <div
      className={`viewer${expanded ? " viewer--expanded" : ""}`}
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded ? "true" : undefined}
      aria-label={expanded ? "Büyütülmüş 3D görünüm" : undefined}
    >
      <canvas
        ref={canvasRef}
        className="viewer__canvas"
        // The cloud is data, not decoration: it needs a name in the
        // accessibility tree, and the count is the honest one-line summary.
        role="img"
        aria-label={`Seyrek 3D nokta bulutu, ${cloud.count.toLocaleString("tr-TR")} nokta`}
      />
      <ViewerToolbar
        onPreset={view.setOrientation}
        onFit={view.fit}
        onReset={view.reset}
        onZoomIn={view.zoomIn}
        onZoomOut={view.zoomOut}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((current) => !current)}
        onCapture={() => void capture()}
        capturing={capturing}
        captureFailed={captureFailed}
        caption="Bu, kameraların çözdüğü 3D noktalardır; bir yüzey modeli değildir."
        hint={VIEWER_HINT}
        // Only here: a mesh has no points, so a point size would be a control
        // that changes nothing on the model viewer.
        extra={<PointSizeControl />}
      />
    </div>
  );
}

/**
 * How big a point is drawn, where somebody is looking at points.
 *
 * The setting existed - under Ayarlar → 3D Görüntüleyici - which is the wrong
 * place to adjust something you can only judge by watching it change. This is
 * the same preference, not a second one: it reads and writes
 * `viewerPreferences`, so the settings screen and the viewer cannot disagree,
 * and changing it here updates the settings screen too.
 */
function PointSizeControl() {
  const [size, setSize] = useState<PointSize>(() => readPointSize());

  // Kept in step with a change made anywhere else in the document - the
  // settings screen is the same SPA.
  useEffect(() => {
    const apply = () => setSize(readPointSize());
    window.addEventListener(POINT_SIZE_CHANGED, apply);
    window.addEventListener("storage", apply);
    return () => {
      window.removeEventListener(POINT_SIZE_CHANGED, apply);
      window.removeEventListener("storage", apply);
    };
  }, []);

  return (
    <label className="viewer__pointsize">
      <span>Nokta boyutu</span>
      <select
        value={size}
        onChange={(event) => {
          const next = event.target.value as PointSize;
          setSize(next);
          writePointSize(next);
        }}
      >
        <option value="small">Küçük</option>
        <option value="medium">Orta</option>
        <option value="large">Büyük</option>
      </select>
    </label>
  );
}
